/**
 * @render react
 * @name GoTo
*/

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Icon } from 'Components/atoms/Icon';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * GoTo component
 *
 * @param {string} title - section title
 * @param {string} url - section url
 * @param {string} icon - icon src attribute
 * @param {string} label - CTA text
 * @param {string} direction - prev or next property of directions obj that will override defaultLabel/defaultIcon if icon and label not provided
 * @param {Object} directions - configuration object with predifined values of label/icon depeneding on direction
 * @param {string} defaultLabel - default value if label hacn't been set
 * @param {string} defaultIcon - default value if icon hacn't been set
 * @param {boolean} loadStyles - whether to apply the class names for styling
 * @param {Object} extraClassNames - extra classes to be added to the component
*/

const GoTo = (
  {
    title,
    url,
    icon,
    label,
    direction,
    directions,
    defaultLabel,
    defaultIcon,
    loadStyles,
    extraClassNames,
    context: { cssPrefix },
  },
) => {
  let goToLabel = label;
  let goToIcon = icon ? <Icon icon={icon} /> : null;

  if (directions[direction]) {
    goToLabel = label || directions[direction].label;
    goToIcon = goToIcon || directions[direction].icon;
  }

  /*
  * If direction (prev or next) has been set and label or icon has not,
  * defined in directions object values of label and icon will be used.
  * If direction, label, and icon have not been set for the component,
  * defaultLabel and defaultIcon will be used.
  */
  return (
    <div className={classNames({
      [`${cssPrefix}__go-to`]: loadStyles,
      ...extraClassNames,
    })}
    >
      <span className={`${cssPrefix}__go-to--label`}>{goToLabel || defaultLabel }</span>
      <span className={`${cssPrefix}__go-to--icon`}>{goToIcon || defaultIcon }</span>
      <span className={`${cssPrefix}__go-to--title`}><Link href={url}>{title}</Link></span>
    </div>
  );
};

/**
 * Valid props
 */
GoTo.propTypes = {
  title: PropTypes.string,
  url: PropTypes.string,
  icon: PropTypes.string,
  label: PropTypes.string,
  defaultLabel: PropTypes.string,
  defaultIcon: PropTypes.string,
  direction: PropTypes.string,
  directions: PropTypes.shape(),
  context: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
};

/**
 * Default props
 */
GoTo.defaultProps = {
  title: '',
  url: '',
  icon: '',
  label: '',
  defaultLabel: 'Go to',
  defaultIcon: '>',
  direction: '',
  directions: {
    next: {
      label: 'Go to',
      icon: '>',
    },
    prev: {
      label: 'Go back to',
      icon: '<',
    },
  },
  context: {},
  loadStyles: true,
  extraClassNames: {},
};

export default withPolaris(GoTo);
